import React, { useState, useEffect } from 'react';
import {
  Box, Typography, IconButton, CircularProgress,
  Dialog, DialogContent,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CloseOutlined, MeetingRoomOutlined, OndemandVideoOutlined, StorefrontOutlined,
  ViewCarouselOutlined,
} from '@mui/icons-material';
import { PRIMARY } from './constants';

const PLENARY_COLOR    = '#8b4fcc';
const EXHIBITION_COLOR = '#0d7a5f';

function toSlug(s) {
  return (s || '').toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

const CAROUSEL_COLOR = '#c2410c';

const TYPES = [
  {
    value: 'lobby',
    label: 'Lobby',
    color: PRIMARY,
    icon: <MeetingRoomOutlined sx={{ fontSize: 26 }} />,
    desc: 'An interactive room with hotspots, images and navigation.',
    badge: null,
  },
  {
    value: 'plenary',
    label: 'Plenary',
    color: PLENARY_COLOR,
    icon: <OndemandVideoOutlined sx={{ fontSize: 26 }} />,
    desc: 'Lobby pre-seeded with a livestream player and live chat.',
    badge: 'PLENARY',
  },
  {
    value: 'exhibition',
    label: 'Exhibition Hall',
    color: EXHIBITION_COLOR,
    icon: <StorefrontOutlined sx={{ fontSize: 26 }} />,
    desc: 'Lobby pre-seeded with a centered exhibitor grid.',
    badge: 'HALL',
  },
  {
    value: 'carousel',
    label: 'Carousel',
    color: CAROUSEL_COLOR,
    icon: <ViewCarouselOutlined sx={{ fontSize: 26 }} />,
    desc: 'Multiple slides each with their own background and hotspots.',
    badge: 'SLIDES',
  },
];

const inputSx = {
  width: '100%', border: '1.5px solid #e4e9f0', borderRadius: '8px',
  padding: '9px 12px', fontSize: '0.88rem', outline: 'none',
  fontFamily: 'inherit', background: '#f8f9fb', boxSizing: 'border-box',
  transition: 'border-color 0.15s',
};

export function CreateLobbyOrPlenaryDialog({ open, onClose, onLobbyCreated, event }) {
  const [type,    setType]    = useState('lobby');
  const [name,    setName]    = useState('');
  const [slug,    setSlug]    = useState('');
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');

  // Auto-derive slug from name whenever name changes
  useEffect(() => {
    setSlug(toSlug(name));
  }, [name]);

  const reset = () => {
    setType('lobby'); setName(''); setSlug('');
    setSaving(false); setError('');
  };

  const handleClose = () => { reset(); onClose(); };

  const accentColor =
    type === 'plenary'    ? PLENARY_COLOR    :
    type === 'exhibition' ? EXHIBITION_COLOR :
    type === 'carousel'   ? CAROUSEL_COLOR   :
    PRIMARY;

  const handleCreate = async () => {
    if (!name.trim()) { setError('Lobby name is required.'); return; }
    setSaving(true); setError('');
    try {
      const finalSlug = slug.trim() || toSlug(name.trim()) || `lobby-${Date.now().toString(36)}`;
      const payload = {
        name:               name.trim(),
        slug:               finalSlug,
        lobbyType:          type,
        isDefault:          false,
        isActive:           true,
        bgType:             'image',
        bgUrl:              null,
        showIntro:          false,
        introVideoUrl:      null,
        transitionVideoUrl: null,
        returnVideoUrl:     null,
        event:              event._id,
        hotspots:           [],
        elements:           [],
        slides:             type === 'carousel' ? [] : undefined,
        _seedType:          type,
      };
      await onLobbyCreated(payload);
      handleClose();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'An error occurred.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open} onClose={handleClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' } }}
    >
      {/* Header */}
      <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: '1px solid #f0f2f5', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#1a1a2e', mb: 0.25 }}>
            Add Lobby
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: '#9ea8b3' }}>
            Choose a starting template
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} sx={{ color: '#9ea8b3', mt: '-2px' }}>
          <CloseOutlined sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

        {/* Error */}
        {error && (
          <Box sx={{ px: 1.5, py: 1, borderRadius: '8px', background: alpha('#dc2626', 0.07), border: `1px solid ${alpha('#dc2626', 0.2)}` }}>
            <Typography sx={{ fontSize: '0.78rem', color: '#dc2626' }}>{error}</Typography>
          </Box>
        )}

        {/* Type cards — 2×2 grid */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
          {TYPES.map(t => {
            const active = type === t.value;
            return (
              <Box
                key={t.value}
                onClick={() => { setType(t.value); setError(''); }}
                sx={{
                  flex: 1, borderRadius: '10px', p: '14px 12px 12px',
                  border: `1.5px solid ${active ? t.color : '#e4e9f0'}`,
                  background: active ? alpha(t.color, 0.05) : '#fff',
                  cursor: 'pointer', transition: 'all 0.12s',
                  boxShadow: active ? `0 0 0 3px ${alpha(t.color, 0.12)}` : 'none',
                  '&:hover': { borderColor: active ? t.color : alpha(t.color, 0.4) },
                }}
              >
                {t.badge && (
                  <Box sx={{ display: 'inline-flex', mb: 0.75, px: 0.6, py: 0.15, borderRadius: '3px', background: active ? t.color : '#e4e9f0', color: active ? '#fff' : '#9ea8b3', fontSize: '0.5rem', fontWeight: 700, letterSpacing: '0.06em' }}>
                    {t.badge}
                  </Box>
                )}
                <Box sx={{ color: active ? t.color : '#b0bac8', mb: 0.75, transition: 'color 0.12s', display: 'flex' }}>
                  {t.icon}
                </Box>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: active ? 700 : 600, color: active ? t.color : '#3c4555', mb: 0.35 }}>
                  {t.label}
                </Typography>
                <Typography sx={{ fontSize: '0.65rem', color: '#9ea8b3', lineHeight: 1.45 }}>
                  {t.desc}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Divider */}
        <Box sx={{ borderTop: '1px solid #f0f2f5', mx: -3 }} />

        {/* Name field only */}
        <Box>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#3c4555', mb: 0.75 }}>
            Lobby name <Box component="span" sx={{ color: '#ef4444' }}>*</Box>
          </Typography>
          <input
            autoFocus
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleCreate(); }}
            placeholder={type === 'plenary' ? 'e.g. Main Stage' : type === 'exhibition' ? 'e.g. Exhibitor Hall' : type === 'carousel' ? 'e.g. Welcome Slides' : 'e.g. Main Lobby'}
            style={inputSx}
            onFocus={el => el.target.style.borderColor = accentColor}
            onBlur={el => el.target.style.borderColor = '#e4e9f0'}
          />
        </Box>

        {/* Slug preview */}
        {slug && (
          <Typography sx={{ fontSize: '0.65rem', color: '#b0bac8', mt: -1.5 }}>
            Slug: <Box component="span" sx={{ fontFamily: 'monospace', color: '#7a8899' }}>{slug}</Box>
          </Typography>
        )}

      </DialogContent>

      {/* Footer */}
      <Box sx={{ px: 3, py: 2, borderTop: '1px solid #f0f2f5', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: '0.72rem', color: '#b0bac8' }}>
          {event?.name || 'Event'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Box onClick={handleClose} sx={{ px: 2, py: 0.85, borderRadius: '8px', border: '1.5px solid #e4e9f0', background: '#fff', fontSize: '0.8rem', fontWeight: 600, color: '#5a6474', cursor: 'pointer', '&:hover': { background: '#f8f9fb' } }}>
            Cancel
          </Box>
          <Box
            onClick={handleCreate}
            sx={{
              px: 2.5, py: 0.85, borderRadius: '8px',
              background: saving ? '#9ea8b3' : accentColor,
              fontSize: '0.8rem', fontWeight: 700, color: '#fff',
              cursor: saving ? 'default' : 'pointer',
              display: 'flex', alignItems: 'center', gap: 0.75,
              transition: 'all 0.15s', '&:hover': { opacity: saving ? 1 : 0.88 },
            }}
          >
            {saving && <CircularProgress size={12} sx={{ color: '#fff' }} />}
            {saving ? 'Creating…' : `Create ${TYPES.find(t => t.value === type)?.label}`}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}